import type { ApiClient } from '@carbonwebmail/api-client'
import type { EmailBodyDto } from '@carbonwebmail/contracts'
import { designedBody } from '@carbonwebmail/domain'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Design } from '../../ui/compose/ComposeCanvas.js'

/**
 * Composing one message, whether it belongs to a campaign, to a template, or to
 * nothing yet.
 *
 * **One screen for all three.** A campaign's message and a template's are the
 * same thing — an `EmailBody` — and the only difference is which row it is saved
 * back to. Two composers would be two places for the builder's configuration,
 * the unsaved-changes guard and the save path to drift, and the one that drifted
 * would be the one somebody used.
 *
 * **What is open is loaded here, not passed in.** Compose is reached by url, so
 * it has to survive a refresh and a pasted link — which means it fetches what it
 * is composing rather than expecting a screen to have handed it over.
 *
 * **Unsaved is a real state and is tracked.** The builder is uncontrolled and
 * tells us after every edit; a person who closes the tab mid-design should be
 * asked, and a person who has changed nothing should not be.
 */

/** A campaign's message, or a template's. The url says which. */
export type Composing = 'campaign' | 'template'

/**
 * The url segment for a template that does not exist yet.
 *
 * Not exported: only this hook compares against it. The links that point here
 * spell it out the way every other route segment in the application is spelt
 * out, rather than importing a view model to build a path.
 */
const NEW = 'new'

export type ComposeState =
  | { readonly status: 'opening' }
  | {
      readonly status: 'ready'
      /** Shown in the toolbar, and editable for a template. */
      readonly name: string
      readonly subject: string
      readonly body: EmailBodyDto
    }
  | { readonly status: 'not-found' }
  /** A campaign that has been queued. Its message is frozen and cannot change. */
  | { readonly status: 'sent' }
  | { readonly status: 'failed' }

export type SaveState =
  | { readonly status: 'unsaved' }
  | { readonly status: 'saving' }
  | { readonly status: 'saved' }
  | { readonly status: 'refused'; readonly detail: string }

export function useCompose({
  client,
  composing,
  id,
}: {
  readonly client: ApiClient
  readonly composing: Composing
  readonly id: string
}) {
  const fresh = composing === 'template' && id === NEW

  const [state, setState] = useState<ComposeState>(
    // Nothing to fetch for a design that does not exist yet, and a moment of
    // "Opening…" over an empty canvas is a moment of looking broken.
    fresh
      ? { status: 'ready', name: '', subject: '', body: { kind: 'designed', html: '', source: '' } }
      : { status: 'opening' },
  )
  const [save, setSave] = useState<SaveState>({ status: 'saved' })

  /**
   * The design as the builder last reported it.
   *
   * A ref rather than state: it changes on every keystroke and nothing here
   * renders from it, so putting it in state would re-render the toolbar a
   * hundred times a minute to display nothing new.
   */
  const design = useRef<Design | undefined>(undefined)

  useEffect(() => {
    if (fresh) return
    let current = true

    async function open() {
      try {
        if (composing === 'campaign') {
          const campaign = await client.campaignContent(id)
          if (!current) return
          // A queued campaign's message is frozen — two different emails under
          // one campaign id would make the send log a record of something that
          // never happened.
          if (campaign.status !== 'draft') {
            setState({ status: 'sent' })
            return
          }
          setState({
            status: 'ready',
            name: campaign.name,
            subject: campaign.subject,
            body: campaign.body ?? { kind: 'designed', html: '', source: '' },
          })
          return
        }

        const templates = await client.templates()
        if (!current) return
        const template = templates.templates.find((one) => one.templateId === id)
        setState(
          template === undefined
            ? { status: 'not-found' }
            : {
                status: 'ready',
                name: template.name,
                subject: template.subject,
                body: template.body,
              },
        )
      } catch {
        if (current) setState({ status: 'failed' })
      }
    }

    void open()
    return () => {
      current = false
    }
  }, [client, composing, fresh, id])

  /** Told after every edit, so "unsaved" means something. */
  const changed = useCallback((next: Design) => {
    design.current = next
    setSave({ status: 'unsaved' })
  }, [])

  /**
   * Told once, when the builder has parsed what it was given.
   *
   * Deliberately not `changed`: a draft somebody has only opened has nothing to
   * lose, and prompting them on the way out is how a prompt stops being read.
   * What it does fix is that the design was previously unknown until the first
   * edit — so Save & quit on an untouched draft returned without saving and
   * without saying anything, and the button looked broken.
   */
  const openedWith = useCallback((next: Design) => {
    design.current = next
  }, [])

  /**
   * The name and subject, edited in the toolbar.
   *
   * A template's are part of composing it — a design with no name is one nobody
   * finds again — so they are edited here rather than on a separate form. A
   * campaign's are not: they belong to the campaign, beside its audience and its
   * from address, and this screen only ever sends back what it read.
   */
  const rename = useCallback((field: 'name' | 'subject', value: string) => {
    setState((current) => (current.status === 'ready' ? { ...current, [field]: value } : current))
    setSave({ status: 'unsaved' })
  }, [])

  const commit = useCallback(async (): Promise<string | undefined> => {
    if (state.status !== 'ready' || design.current === undefined) return undefined
    const body = designedBody(design.current.html, design.current.source)

    if (composing === 'template' && (state.name.trim() === '' || state.subject.trim() === '')) {
      setSave({
        status: 'refused',
        detail: 'Give this a name and a subject before saving it.',
      })
      return undefined
    }

    setSave({ status: 'saving' })
    try {
      if (composing === 'campaign') {
        const content = await client.campaignContent(id)
        const outcome = await client.rewriteCampaign(id, {
          // Everything else is left exactly as it was. Compose edits the
          // message; a campaign's name, audience and addresses belong to the
          // screen that owns them, and sending stale copies of them back here
          // is how an edit in one place quietly undoes an edit in another.
          name: content.name,
          segmentId: content.segmentId,
          fromName: content.fromName,
          fromEmail: content.fromEmail,
          replyTo: content.replyTo,
          subject: content.subject,
          body,
        })
        if (outcome.status === 'not-a-draft') {
          setSave({
            status: 'refused',
            detail: 'This campaign was queued while you were working. Its message is now frozen.',
          })
          return undefined
        }
        setSave({ status: 'saved' })
        return id
      }

      const outcome = await client.saveTemplate(
        { name: state.name.trim(), subject: state.subject.trim(), body },
        fresh ? undefined : id,
      )
      if (outcome.status === 'name-taken') {
        setSave({ status: 'refused', detail: 'A template with that name already exists.' })
        return undefined
      }

      setSave({ status: 'saved' })
      return outcome.templateId
    } catch {
      setSave({
        status: 'refused',
        detail: 'We could not save that. Please try again in a moment.',
      })
      return undefined
    }
  }, [client, composing, fresh, id, state])

  /**
   * The browser's own "leave site?" prompt, for the tab being closed.
   *
   * Only while there is something to lose. A prompt on every exit is one people
   * learn to dismiss, and then it does not protect the time it was for.
   */
  useEffect(() => {
    if (save.status !== 'unsaved') return

    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [save.status])

  return { state, save, fresh, changed, openedWith, rename, commit, design }
}
